import React, { Suspense, lazy, useCallback, useEffect, useRef } from "react";
import { useAtom } from "jotai";
import SVGIcon from "../../svg";
import { activePopupState } from "../../../state/components/popup";
import Alert from "./alert";
import Confirm from "./confirm";

type PopupComponent = React.ComponentType<{ hide: () => void }>;

const named = (loader: () => Promise<Record<string, unknown>>, key: string) =>
  lazy(() => loader().then((m) => ({ default: m[key] as PopupComponent })));

const AddAccountsPopup = named(
  () => import("./add-accounts-popup"),
  "AddAccountsPopup",
);
const AccountSourcesPopup = named(
  () => import("./account-sources-popup"),
  "AccountSourcesPopup",
);
const AccountLogPopup = named(
  () => import("./account-log-popup"),
  "AccountLogPopup",
);
const AccountSettingsPopup = named(
  () => import("./account-settings-popup"),
  "AccountSettingsPopup",
);
const ScheduledOutboundPopup = named(
  () => import("./scheduled-outbound-popup"),
  "ScheduledOutboundPopup",
);
const SetAccountProxyPopup = named(
  () => import("./set-account-proxy-popup"),
  "SetAccountProxyPopup",
);

const FOCUSABLE =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

const Popups = () => {
  const [activePopup, setActivePopup] = useAtom(activePopupState(null));
  const containRef = useRef<HTMLDivElement | null>(null);
  const lastFocusedRef = useRef<Element | null>(null);

  const isOpen = !!(activePopup && activePopup.allow);

  const hide = useCallback(() => {
    if (activePopup) {
      const onClose = activePopup.onClose;
      setActivePopup({ ...activePopup, active: false, allow: false });
      if (onClose) setTimeout(onClose, 300);
    }
  }, [activePopup, setActivePopup]);

  useEffect(() => {
    if (activePopup && !activePopup.allow) {
      setActivePopup({ ...activePopup, allow: true });
    }
  }, [activePopup, setActivePopup]);

  useEffect(() => {
    if (!isOpen) return;
    lastFocusedRef.current = document.activeElement;
    const node = containRef.current;
    const first = node?.querySelector<HTMLElement>(FOCUSABLE);
    (first || node)?.focus();
    return () => {
      const previous = lastFocusedRef.current as HTMLElement | null;
      if (previous && typeof previous.focus === "function") previous.focus();
    };
  }, [isOpen, activePopup?.id]);

  useEffect(() => {
    if (!isOpen) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.stopPropagation();
        hide();
        return;
      }
      if (event.key !== "Tab") return;
      const items = Array.from(
        containRef.current?.querySelectorAll<HTMLElement>(FOCUSABLE) || [],
      ).filter((el) => el.offsetParent !== null);
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [isOpen, hide]);

  const renderContain = () => {
    switch (activePopup && activePopup.id) {
      case "alert":
        return <Alert hide={hide} />;
      case "confirm":
        return <Confirm hide={hide} />;
      case "add_accounts":
        return <AddAccountsPopup hide={hide} />;
      case "account_sources":
        return <AccountSourcesPopup hide={hide} />;
      case "account_log":
        return <AccountLogPopup hide={hide} />;
      case "account_settings":
        return <AccountSettingsPopup hide={hide} />;
      case "scheduled_outbound":
        return <ScheduledOutboundPopup hide={hide} />;
      case "set_account_proxy":
        return <SetAccountProxyPopup hide={hide} />;
      default:
        return null;
    }
  };

  return (
    <div
      className={
        "jf-popup " +
        (isOpen ? "show" : "hide") +
        (activePopup && activePopup.id ? " jf-popup-" + activePopup.id : "")
      }
    >
      <div
        className={"jf-popup-cover " + (isOpen ? "show" : "hide")}
        onClick={hide}
        aria-hidden="true"
      />
      <div className="jf-popup-transfer">
        <div
          className="jf-popup-contain"
          ref={containRef}
          role="dialog"
          aria-modal="true"
          aria-label={activePopup?.title || "Dialog"}
          tabIndex={-1}
        >
          <button
            className="jf-no-style-btn jf-popup-close-btn"
            type="button"
            aria-label="Close dialog"
            onClick={hide}
          >
            <SVGIcon iconname="close-icon" />
          </button>
          {isOpen && (
            <Suspense
              fallback={
                <div
                  className="jf-popup-loading"
                  role="status"
                  aria-live="polite"
                >
                  Loading…
                </div>
              }
            >
              {renderContain()}
            </Suspense>
          )}
        </div>
      </div>
    </div>
  );
};

export default Popups;
